import { Navigate, Route, Routes } from "react-router-dom";
import { Shell } from "../components/Shell";
import { landingPathFor } from "../lib/landing";
import { Attendance } from "../pages/Attendance";
import { Helpline } from "../pages/Helpline";
import { SupportTicketDetail } from "../pages/SupportTicketDetail";
import { SupportTicketForm } from "../pages/SupportTicketForm";
import { History } from "../pages/History";
import { Login } from "../pages/Login";
import { ModulePlayer } from "../pages/ModulePlayer";
import { ModuleCompletion } from "../pages/ModuleCompletion";
import { PDDayDetail } from "../pages/PDDayDetail";
import { PDDayModules } from "../pages/PDDayModules";
import { PDDayProgress } from "../pages/PDDayProgress";
import { PDDays } from "../pages/PDDays";
import { PDDaySummary } from "../pages/PDDaySummary";
import { Placeholder } from "../pages/Placeholder";
import { Profile } from "../pages/Profile";
import { Schedule } from "../pages/Schedule";
import { ProtectedRoute } from "./ProtectedRoute";
import type { Session } from "../types";

export interface AppRoutesProps {
  session: Session | null;
  onLogin: (session: Session) => void;
  onLogout: () => void;
}

// Every screen owns a named path — no anonymous index routes — so any view
// can be linked to, deep-linked into, and restored after a reload. Both
// facilitators and teachers land on the PD-days list first — see
// lib/landing.ts.
export function AppRoutes({ session, onLogin, onLogout }: AppRoutesProps) {
  const landing = landingPathFor(session?.role);

  return (
    <Routes>
      <Route
        path="/login"
        element={session ? <Navigate to={landing} replace /> : <Login onLogin={onLogin} />}
      />
      <Route
        element={
          <ProtectedRoute session={session}>
            <Shell role={session?.role ?? "teacher"} onLogout={onLogout} />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Navigate to={landing} replace />} />

        {/* Facilitator */}
        <Route path="/pd-days" element={<PDDays />} />
        <Route path="/pd-day/:sessionID" element={<PDDayDetail />} />
        <Route path="/attendance/:sessionID" element={<Attendance />} />
        <Route path="/pd-day-progress/:sessionID" element={<PDDayProgress />} />

        {/* Teacher */}
        {/* The old download screen; the header now saves lessons in the background. */}
        <Route path="/home" element={<Navigate to="/pd-days" replace />} />
        <Route path="/pd-day-modules/:sessionID" element={<PDDayModules />} />
        <Route path="/modules/:moduleID" element={<ModulePlayer />} />
        <Route path="/modules/:moduleID/complete" element={<ModuleCompletion />} />
        <Route path="/history" element={<History />} />
        <Route path="/pd-day-summary/:sessionID" element={<PDDaySummary />} />

        {/* Shared */}
        <Route path="/profile" element={session ? <Profile session={session} /> : null} />
        <Route path="/training" element={<Placeholder titleKey="navTraining" />} />
        <Route path="/helpline" element={<Helpline />} />
        <Route path="/helpline/new" element={<SupportTicketForm />} />
        <Route path="/helpline/tickets/:ticketID" element={<SupportTicketDetail />} />
        <Route path="/schedule" element={<Schedule />} />

        <Route path="*" element={<Navigate to={landing} replace />} />
      </Route>
    </Routes>
  );
}
